import type {
  ITableCell,
  ITableCellContentAppliableSettings,
} from "@library-elements/table";
import { TEXT_STYLE_TAGS, TEXT_STYLES } from "../../text";
import { stripStyleDecls } from "./strip-style-decls";

interface IProps extends ITableCellContentAppliableSettings {
  cell: ITableCell;
}

export const tableApplyFroalaContentSettings = ({
  cell,
  textColor,
  fontSize,
  textAlign,
  textOrientation,
  bold,
  italic,
  underline,
  strikethrough,
  textStyle,
  lineHeight,
  clearFormatting,
}: IProps) => {
  const toChange = {
    textColor: textColor != null,
    fontSize: fontSize != null,
    textAlign: textAlign != null,
    textOrientation: textOrientation != null,
    bold: bold != null,
    italic: italic != null,
    underline: underline != null,
    textStyle: textStyle != null,
    lineHeight: lineHeight != null,
    strikethrough: strikethrough != null,
  };

  const styleCleanupMap = [
    { prop: "textColor", css: ["color"] },
    { prop: ["fontSize", "textStyle"], css: ["font-size"] },
    { prop: "textStyle", css: ["line-height"] },
    { prop: "textAlign", css: ["text-align"] },
    { prop: "textOrientation", css: ["text-align", "direction"] },
    { prop: "lineHeight", css: ["line-height"] },
  ];

  styleCleanupMap.forEach(({ prop, css }) => {
    let shouldClean = clearFormatting;

    if (!shouldClean) {
      if (Array.isArray(prop)) {
        shouldClean = prop.some((p) => toChange[p as keyof typeof toChange]);
      } else shouldClean = toChange[prop as keyof typeof toChange];
    }

    if (shouldClean) {
      cell.content = stripStyleDecls(cell.content, css);
    }
  });

  const tmpTree = new DOMParser().parseFromString(
    cell.content.length === 0 ? "<p><br></p>" : cell.content,
    "text/html",
  );
  normalizeToBlocks(tmpTree);
  const blocks = tmpTree.body.querySelectorAll(
    "p, li, h1, h2, h3, h4, h5, h6",
  ) as unknown as HTMLElement[];

  blocks.forEach((block) => {
    let span = tmpTree.createElement("span");
    let hasNewSpan = true;

    if (
      block.childNodes.length === 1 &&
      block.childNodes[0].nodeName === "SPAN"
    ) {
      hasNewSpan = false;
      span = block.childNodes[0] as HTMLElement;
    }

    if (toChange.textStyle) {
      const vars = TEXT_STYLES[textStyle!];
      block.style.setProperty("font-size", `var(--${vars.size})`);
      block.style.setProperty("color", `var(--${vars.color})`);
      block.style.setProperty("font-family", `var(--${vars.font})`);
      block.style.setProperty("font-weight", `var(--${vars.weight})`);

      if (block.tagName !== "LI") {
        const newEl = document.createElement(
          TEXT_STYLE_TAGS[textStyle as keyof typeof TEXT_STYLE_TAGS] ?? "p",
        );
        for (let i = 0; i < block.attributes.length; i++) {
          const attribute = block.attributes[i];
          newEl.setAttribute(attribute.name, attribute.value);
        }
        while (block.firstChild) {
          newEl.appendChild(block.firstChild);
        }
        block.replaceWith(newEl);
      }
    }

    if (toChange.lineHeight) {
      block.style.setProperty("line-height", lineHeight as string);
    }

    if (toChange.textColor) {
      block.style.setProperty("color", textColor as string);
      span.style.setProperty("color", textColor as string);
    }

    if (toChange.fontSize) {
      span.style.setProperty("font-size", fontSize as string);
    }

    if (toChange.textAlign) {
      block.style.setProperty("text-align", textAlign as string);
    }

    if (toChange.textOrientation) {
      block.style.setProperty(
        "text-align",
        textOrientation === "rtl" ? "right" : "left",
      );
      block.style.setProperty("direction", textOrientation as string);
    }

    const formattingRules = [
      { prop: "bold", tag: "strong" },
      { prop: "italic", tag: "em" },
      { prop: "underline", tag: "u" },
      { prop: "strikethrough", tag: "s" },
    ];

    formattingRules.forEach(({ prop, tag }) => {
      if (toChange[prop as keyof typeof toChange] || clearFormatting) {
        removeWrapperEL(block, tag);
        if (
          (prop === "bold" && bold) ||
          (prop === "italic" && italic) ||
          (prop === "underline" && underline) ||
          (prop === "strikethrough" && strikethrough)
        ) {
          wrapIn(tmpTree, block, tag);
        }
      }
    });

    if (hasNewSpan) {
      Array.from(block.childNodes).forEach((child) =>
        span.appendChild(child.cloneNode(true)),
      );
      block.innerHTML = "";
      block.appendChild(span);
    }
  });

  cell.content =
    tmpTree.body.firstChild instanceof HTMLElement
      ? tmpTree.body.innerHTML
      : cell.content;
};

const BLOCK_TAGS = new Set(["P", "UL", "OL", "LI", "H1", "H2", "H3", "H4", "H5", "H6"]);

// Cell content is expected to be Froala-shaped (block-wrapped), but imported
// or pasted cells can carry bare text or <div> wrappers, which the block pass
// would otherwise silently skip: unwrap top-level divs, then wrap loose runs
// in <p> so every piece of text lives under a styleable block.
const normalizeToBlocks = (doc: Document) => {
  const body = doc.body;
  let guard = 0;
  while (guard++ < 50) {
    const div = Array.from(body.children).find((el) => el.tagName === "DIV");
    if (!div) break;
    const hasBlockChild = Array.from(div.children).some(
      (el) => BLOCK_TAGS.has(el.tagName) || el.tagName === "DIV",
    );
    if (hasBlockChild) {
      while (div.firstChild) body.insertBefore(div.firstChild, div);
      div.remove();
      continue;
    }
    // A block-less div is one line of content: convert it to its own <p> so
    // adjacent divs don't collapse into a single bucketed paragraph.
    const p = doc.createElement("p");
    for (let i = 0; i < div.attributes.length; i++) {
      const attribute = div.attributes[i];
      p.setAttribute(attribute.name, attribute.value);
    }
    while (div.firstChild) p.appendChild(div.firstChild);
    div.replaceWith(p);
  }
  let bucket: HTMLElement | null = null;
  for (const node of Array.from(body.childNodes)) {
    const isBlock =
      node.nodeType === Node.ELEMENT_NODE &&
      BLOCK_TAGS.has((node as HTMLElement).tagName);
    if (isBlock) {
      bucket = null;
      continue;
    }
    if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
      continue;
    }
    if (!bucket) {
      bucket = doc.createElement("p");
      body.insertBefore(bucket, node);
    }
    bucket.appendChild(node);
  }
};

const removeWrapperEL = (target: HTMLElement, selector: string) => {
  const toRemove = target.querySelectorAll(selector);
  toRemove.forEach((strong) => {
    const parent = strong.parentNode;
    while (strong.firstChild) {
      parent?.insertBefore(strong.firstChild, strong);
    }
    strong.remove();
  });
};

const wrapIn = (doc: Document, target: HTMLElement, el: string) => {
  const wrapper = doc.createElement(el);
  wrapper.setAttribute("data-from-table-header", "true");
  Array.from(target.childNodes).forEach((child) =>
    wrapper.appendChild(child.cloneNode(true)),
  );
  target.innerHTML = "";
  target.appendChild(wrapper);
};
